import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/Table';
import { financeReportService } from '../../services/financeReport.service';
import { FileText } from 'lucide-react';
import { InvoiceStatus } from '../../types';

export const TripFinanceReportTab: React.FC = () => {
  const [startDate, setStartDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [queryDates, setQueryDates] = useState({ startDate, endDate });

  const { data: trips, isLoading, error } = useQuery({
    queryKey: ['finance-trips-report', queryDates.startDate, queryDates.endDate],
    queryFn: () => financeReportService.getTripsReport(queryDates.startDate, queryDates.endDate),
  });

  const handleGenerate = () => {
    setQueryDates({ startDate, endDate });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-TZ', {
      style: 'currency',
      currency: 'TZS',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getInvoiceStatusVariant = (status?: InvoiceStatus) => {
    if (!status) return 'default';
    switch (status) {
      case InvoiceStatus.PAID:
        return 'success';
      case InvoiceStatus.ISSUED:
        return 'warning';
      case InvoiceStatus.DRAFT:
        return 'info';
      case InvoiceStatus.CANCELLED:
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Input
          label="Start Date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
        />
        <Input
          label="End Date"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
        />
        <div className="flex items-end">
          <Button onClick={handleGenerate} className="w-full">
            <FileText size={16} className="mr-2" />
            Generate Report
          </Button>
        </div>
      </div>

      {isLoading && (
        <div className="text-center py-8 text-gray-500">Loading trip reports...</div>
      )}

      {error && (
        <div className="text-center py-8 text-error">Failed to load trip reports</div>
      )}

      {trips && trips.length === 0 && (
        <div className="text-center py-8 text-gray-500">No trips found for the selected date range</div>
      )}

      {trips && trips.length > 0 && (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Trip Number</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Route</TableHead>
                <TableHead>Vehicle</TableHead>
                <TableHead>Driver</TableHead>
                <TableHead>Invoice</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Invoiced</TableHead>
                <TableHead className="text-right">Paid</TableHead>
                <TableHead className="text-right">Outstanding</TableHead>
                <TableHead className="text-right">Allowances</TableHead>
                <TableHead className="text-right">Net Profit</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {trips.map((trip) => (
                <TableRow key={trip.tripId}>
                  <TableCell className="font-medium">{trip.tripNumber}</TableCell>
                  <TableCell>
                    {format(parseISO(trip.startDate), 'MMM dd, yyyy')}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <div>{trip.origin}</div>
                      <div className="text-gray-500">→ {trip.destination}</div>
                    </div>
                  </TableCell>
                  <TableCell>{trip.vehicleRegistration}</TableCell>
                  <TableCell>{trip.driverName}</TableCell>
                  <TableCell className="text-sm">{trip.invoiceNumber || '-'}</TableCell>
                  <TableCell>
                    {trip.invoiceStatus ? (
                      <Badge variant={getInvoiceStatusVariant(trip.invoiceStatus)}>
                        {trip.invoiceStatus}
                      </Badge>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">{formatCurrency(trip.invoiceAmount)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(trip.totalPayments)}</TableCell>
                  <TableCell className="text-right">
                    <span className={trip.outstandingBalance > 0 ? 'text-yellow-600 font-semibold' : ''}>
                      {formatCurrency(trip.outstandingBalance)}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">{formatCurrency(trip.approvedAllowances)}</TableCell>
                  <TableCell className="text-right">
                    <span className={trip.netProfit > 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                      {formatCurrency(trip.netProfit)}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="text-sm text-gray-600">Total Trips</p>
              <p className="text-xl font-bold">{trips.length}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Invoiced</p>
              <p className="text-xl font-bold">
                {formatCurrency(trips.reduce((sum, t) => sum + t.invoiceAmount, 0))}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Paid</p>
              <p className="text-xl font-bold text-green-600">
                {formatCurrency(trips.reduce((sum, t) => sum + t.totalPayments, 0))}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Net Profit</p>
              <p className="text-xl font-bold text-primary-green">
                {formatCurrency(trips.reduce((sum, t) => sum + t.netProfit, 0))}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
